import _ from 'underscore';
import { getAppStore } from '../../../../app/AppStore';
import { CommonErrorCode, GroupErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { ContentActionNotification} from '../../../../commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem, MultipleItemResult
} from '../../../../commonInfra/contentActions/MultipleItemResult';
import { IUpdateUsersGroupMembershipResult } from '../../../../ts/ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { IUserInfo } from '../../../../ts/models/IUserInfo';
import { L10n } from '../../../../ts/L10n/L10n';
import { Messages } from '../../../../codegen/Localize';
import { Set } from '../../../../ts/models/Set';
import { siteUrlsSelector } from '../../../../app/selectors/SiteUrlsSelectors';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues, MultipleItemResultTranslator
} from '../../../../commonInfra/contentActions/ActionMessages';

function notify(
    result: IUpdateUsersGroupMembershipResult,
    users: Set<IUserInfo>,
    addedToGroupCount: number,
    addedToGroupName: string,
    removedFromGroupCount: number,
    removedFromGroupName: string): void {

    let notification: MultipleItemResultNotificationValues;
    let multipleResultTranslator: MultipleItemResultTranslator<IUserInfo>;

    if (result.errors && result.errors.length && result.errors[0].code === CommonErrorCode.GroupNotFound) {
        // TODO: how can we determine if the "to" group or the "from" group (or both) are not found?
        notification = {
            errorSummary: Messages.errorGroupNotFound({
                ITEM_NAME: L10n.elideIfLong(addedToGroupName || removedFromGroupName)
            })
        };
    }
    else {
        let messageDefaultArgs = {
            USER_NAME: L10n.elideIfLong(nameFor(users.first()))
        };

        // determine result notification strings and args
        if (addedToGroupCount > 0) {
            if (removedFromGroupCount > 0) {
                const usersAddedToGroupsAndRemovedFromGroupsMessageArgs = {
                    ...messageDefaultArgs,
                    ADD_COUNT: addedToGroupCount,
                    ADD_COUNT_LOC: L10n.localizedCount(addedToGroupCount),
                    ADD_NAME: L10n.elideIfLong(addedToGroupName),
                    REMOVE_COUNT: removedFromGroupCount,
                    REMOVE_COUNT_LOC: L10n.localizedCount(removedFromGroupCount),
                    REMOVE_NAME: L10n.elideIfLong(removedFromGroupName),
                };
                multipleResultTranslator = {
                    describeSuccessSummary: (successCount: number) => {
                        let successMessageArgs = {
                            ...usersAddedToGroupsAndRemovedFromGroupsMessageArgs,
                            USER_COUNT: successCount,
                            USER_COUNT_LOC: L10n.localizedCount(successCount)
                        };
                        return Messages.groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups(successMessageArgs);
                    },
                    describeErrorSummary: (errorCount: number) => {
                        let errorMessageArgs = {
                            ...usersAddedToGroupsAndRemovedFromGroupsMessageArgs,
                            USER_COUNT: errorCount,
                            USER_COUNT_LOC: L10n.localizedCount(errorCount)
                        };
                        return Messages.groupMembershipChanged_usersAddedToGroupsAndRemovedFromGroups_failed(errorMessageArgs);
                    },
                    describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
                        return {
                            itemName: errorItem.item.username,
                            itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
                            errorMessage: errorMessageFor(errorItem)
                        };
                    }
                };
            } else {
                const usersAddedToGroupsMessageArgs = {
                    ...messageDefaultArgs,
                    ADD_COUNT: addedToGroupCount,
                    ADD_COUNT_LOC: L10n.localizedCount(addedToGroupCount),
                    ADD_NAME: L10n.elideIfLong(addedToGroupName)
                };
                multipleResultTranslator = {
                    describeSuccessSummary: (successCount: number) => {
                        let successMessageArgs = {
                            ...usersAddedToGroupsMessageArgs,
                            USER_COUNT: successCount,
                            USER_COUNT_LOC: L10n.localizedCount(successCount)
                        };
                        return Messages.groupMembershipChanged_usersAddedToGroups(successMessageArgs);
                      },
                    describeErrorSummary: (errorCount: number) => {
                        let errorMessageArgs = {
                            ...usersAddedToGroupsMessageArgs,
                            USER_COUNT: errorCount,
                            USER_COUNT_LOC: L10n.localizedCount(errorCount)
                        };
                        return Messages.groupMembershipChanged_usersAddedToGroups(errorMessageArgs);
                    },
                    describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
                        return {
                            itemName: errorItem.item.username,
                            itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
                            errorMessage: errorMessageFor(errorItem)
                        };
                    }
                };
            }
        }
        else {
            const usersRemovedFromGroupsMessageArgs = {
                ...messageDefaultArgs,
                REMOVE_COUNT: removedFromGroupCount,
                REMOVE_COUNT_LOC: L10n.localizedCount(removedFromGroupCount),
                REMOVE_NAME: L10n.elideIfLong(removedFromGroupName),
            };

            multipleResultTranslator = {
                describeSuccessSummary: (successCount: number) => {
                    let successMessageArgs = {
                        ...usersRemovedFromGroupsMessageArgs,
                        USER_COUNT: successCount,
                        USER_COUNT_LOC: L10n.localizedCount(successCount)
                    };
                    return Messages.groupMembershipChanged_usersRemovedFromGroups(successMessageArgs);
                },
                describeErrorSummary: (errorCount: number) => {
                    let errorMessageArgs = {
                        ...usersRemovedFromGroupsMessageArgs,
                        USER_COUNT: errorCount,
                        USER_COUNT_LOC: L10n.localizedCount(errorCount)
                    };
                    return Messages.groupMembershipChanged_usersRemovedFromGroups_failed(errorMessageArgs);
                },
                describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
                    return {
                        itemName: errorItem.item.username,
                        itemUrl: getUrlForUser(<IUserInfo>errorItem.item),
                        errorMessage: errorMessageFor(errorItem)
                    };
                }
            };
        }

      // TODO: this doesn't seem right - do the errors apply to users or groups? I don't think
      // we can calculate the success count if errors can apply to groups.
      notification = createMultipleItemResultNotificationValues(
        new MultipleItemResult(users, result),
        multipleResultTranslator
      );
    }
    // pass the notification object to the service for displaying
    ContentActionNotification.displayItemizedToastsForResult(notification);
}

function errorMessageFor(errorItem: IErrorItem<IUserInfo>) {
    switch (errorItem.error.code) {
      case GroupErrorCode.InvalidGroupMembershipUpdate:
        return Messages.errorInvalidAllUsersGroupMembershipUpdate();
      case CommonErrorCode.SiteUserNotFound:
        return Messages.errorUserNotFound();
      case CommonErrorCode.SystemUserNotFound:
        return Messages.errorSystemUserNotFound({ USER_NAME: L10n.elideIfLong(errorItem.item.username) });
      default:
        return Messages.errorUnknown();
    }
}

const nameFor = (user: IUserInfo): string => {
    return Messages.userActionNameFormattedForToast({ DISPLAY_NAME: L10n.elideIfLong(user.displayName), USER_NAME: L10n.elideIfLong(user.username) });
};

// get the URL to the specified user's profile page
const getUrlForUser = (user: IUserInfo) => {
    if (user.username === 'guest' && user.domainName === 'local') {
        return null;
    } else {
        const siteUrls = siteUrlsSelector(getAppStore().getState());
        return siteUrls.userUrl(user.username, user.domainName);
    }
};

export const GroupMembershipUpdateActionNotification = {
    notify
};

export type GroupMembershipUpdateActionNotification = typeof GroupMembershipUpdateActionNotification;
